import { getVoiceAgentState } from './voiceAgentSessionStore';
import { logWebRtcEvent, logWebRtcError } from './voiceWebRtcLog';

function settleAgentIcePrewarmWait() {
  const state = getVoiceAgentState();
  if (!state.agentIcePrewarmWaitResolve) return;
  const resolve = state.agentIcePrewarmWaitResolve;
  state.agentIcePrewarmWaitResolve = null;
  resolve();
}

function isPeerConnectionUsable(pc) {
  if (!pc) return false;
  try {
    return pc.connectionState !== 'closed' && pc.signalingState !== 'closed';
  } catch {
    return false;
  }
}

function readLocalSdpSafe(pc) {
  if (!isPeerConnectionUsable(pc)) return '';
  try {
    return pc.localDescription?.sdp || '';
  } catch {
    // Firefox throws DOMException: Peer connection is closed
    return '';
  }
}

export function releaseAgentIcePrewarmPeerConnection() {
  settleAgentIcePrewarmWait();
  const state = getVoiceAgentState();
  if (!state.agentIcePrewarmPeerConnection) return;
  const pc = state.agentIcePrewarmPeerConnection;
  state.agentIcePrewarmPeerConnection = null;
  try {
    pc.close();
  } catch {
    // ignore
  }
  logWebRtcEvent('sdk.icePrewarmReleased', {});
}

/**
 * Allocate TURN relay before the first INVITE (iceTransportPolicy relay is slow on cold start).
 *
 * @param {RTCConfiguration|undefined} pcConfig
 * @returns {Promise<boolean>}
 */
export async function prewarmAgentIceTransport(pcConfig) {
  if (!pcConfig?.iceServers?.length) {
    return false;
  }
  const state = getVoiceAgentState();
  releaseAgentIcePrewarmPeerConnection();
  const relayOnly = pcConfig.iceTransportPolicy === 'relay';
  const pc = new RTCPeerConnection(pcConfig);
  state.agentIcePrewarmPeerConnection = pc;
  let cancelled = false;
  try {
    pc.createDataChannel('mercately-voice-ice-warm');
    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    if (cancelled || !isPeerConnectionUsable(pc) || state.agentIcePrewarmPeerConnection !== pc) {
      return false;
    }
    await pc.setLocalDescription(offer);
    await new Promise((resolve) => {
      const timeoutMs = relayOnly ? 45_000 : 20_000;
      const deadline = Date.now() + timeoutMs;
      let tickTimer = null;
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        cancelled = true;
        if (tickTimer != null) {
          clearTimeout(tickTimer);
          tickTimer = null;
        }
        if (state.agentIcePrewarmWaitResolve) {
          state.agentIcePrewarmWaitResolve = null;
        }
        clearTimeout(timer);
        try {
          pc.removeEventListener('icegatheringstatechange', onChange);
          pc.removeEventListener('icecandidate', onCandidate);
          pc.removeEventListener('icecandidateerror', onIceError);
          pc.removeEventListener('connectionstatechange', onConnection);
        } catch {
          // ignore
        }
        resolve();
      };

      state.agentIcePrewarmWaitResolve = finish;

      const timer = setTimeout(finish, timeoutMs);

      const onIceError = (event) => {
        logWebRtcEvent('sdk.iceCandidateError', {
          errorCode: event.errorCode,
          errorText: event.errorText,
          url: event.url,
          address: event.address,
          port: event.port
        });
      };
      const onCandidate = () => {
        if (readLocalSdpSafe(pc).includes('typ relay')) {
          finish();
        }
      };
      const onChange = () => {
        try {
          if (pc.iceGatheringState === 'complete') {
            finish();
          }
        } catch {
          finish();
        }
      };
      const onConnection = () => {
        try {
          if (pc.connectionState === 'closed' || pc.connectionState === 'failed') {
            finish();
          }
        } catch {
          finish();
        }
      };
      const tick = () => {
        if (settled || cancelled) return;
        if (!isPeerConnectionUsable(pc) || state.agentIcePrewarmPeerConnection !== pc) {
          finish();
          return;
        }
        if (readLocalSdpSafe(pc).includes('typ relay')) {
          finish();
          return;
        }
        if (Date.now() >= deadline) {
          finish();
          return;
        }
        tickTimer = setTimeout(tick, 200);
      };

      pc.addEventListener('icegatheringstatechange', onChange);
      pc.addEventListener('icecandidate', onCandidate);
      pc.addEventListener('icecandidateerror', onIceError);
      pc.addEventListener('connectionstatechange', onConnection);
      tick();
    });

    if (!isPeerConnectionUsable(pc) || state.agentIcePrewarmPeerConnection !== pc) {
      return false;
    }

    const hasRelay = readLocalSdpSafe(pc).includes('typ relay');
    const turnHasCredential = (pcConfig.iceServers ?? []).some(
      (entry) => typeof entry.credential === 'string' && entry.credential.length > 0
    );
    logWebRtcEvent('sdk.icePrewarm', {
      gatheringState: (() => {
        try {
          return pc.iceGatheringState;
        } catch {
          return 'closed';
        }
      })(),
      relayCandidate: Boolean(hasRelay),
      turnHasCredential
    });
    if (!hasRelay) {
      logWebRtcError(
        'TURN relay not allocated during pre-warm. Audio will fail until coturn is configured (yarn voice:fs:install-coturn on VM, UDP 3478 + 49152-65535 on NSG).'
      );
    }
    return Boolean(hasRelay);
  } catch (error) {
    logWebRtcEvent('sdk.icePrewarmFailed', {
      message: error instanceof Error ? error.message : String(error)
    });
    return false;
  } finally {
    cancelled = true;
    settleAgentIcePrewarmWait();
    if (state.agentIcePrewarmPeerConnection === pc) {
      state.agentIcePrewarmPeerConnection = null;
    }
    try {
      pc.close();
    } catch {
      // ignore
    }
  }
}
